'use client'

import { useMemo, useState } from 'react'

import { Dialog } from '../../components/dialog'
import Faculty from '../../components/icons/faculty'
import LinkedIn from '../../components/icons/linked-in'
import Twitter from '../../components/icons/twitter'
import VerifiedCheck from '../../components/icons/verified-check'
import { SmartImage } from '../../components/smart-image'
import { RichText } from '../helper-components/rich-text'

export interface AuthorCardProps {
  sys: {
    id: string
  }
  bio: {
    json: any | string
    links: any
  }
  name: string
  picture: {
    url: string
  }
  twitterLink?: string
  linkedinLink?: string
  badge?: string
  occupation?: string
}

export const AuthorCardCms = ({ card }: { card: AuthorCardProps }) => {
  const [isOpen, setIsOpen] = useState(false)

  const formatLink = (link: string | undefined) => {
    if (!link) {
      return undefined
    }
    if (link.includes('https://')) {
      return link
    }
    return 'https://' + link
  }

  const links = useMemo(() => {
    const twitterLink = formatLink(card.twitterLink)
    const linkedinLink = formatLink(card.linkedinLink)

    return {
      twitterLink,
      linkedinLink,
    }
  }, [card])

  const renderCard = () => (
    <button
      className="h-full w-full flex flex-col"
      onClick={() => setIsOpen(!isOpen)}
      aria-label={card.name + ' details'}
    >
      {card.picture && (
        <SmartImage
          alt={card.name}
          src={card.picture.url}
          style={{
            objectFit: 'cover',
          }}
          className="aspect-[1/1] w-full"
          aria-label={`image of ${card.name}`}
        />
      )}

      <div className="min-h-[7rem] border border-neutral-300 border-t-transparent p-6 text-start flex-1 w-full">
        <h3 className="font-medium font-sans flex text-xl">
          {card.name}
          {card.badge && (
            <div className="text-primary ml-2">
              <VerifiedCheck />
            </div>
          )}
        </h3>

        <div className="mt-3 font-medium tracking-[0.07rem]">
          {card.occupation}
        </div>
      </div>
    </button>
  )
  return (
    <Dialog
      title={card.name}
      onClose={() => {
        setIsOpen(false)
      }}
      hideTitle
      isOpen={isOpen}
      maxWidth={card.bio?.json ? '1064px' : '500px'}
      trigger={renderCard()}
    >
      <div className="flex flex-col gap-6 px-6 pb-6 sm:flex-row">
        <div className="flex-1">
          {card.picture ? (
            <SmartImage
              alt={card.name}
              src={card.picture.url}
              style={{
                objectFit: 'cover',
              }}
              className="aspect-[1/1]"
              aria-label={`image of ${card.name}`}
            />
          ) : (
            <Faculty
              className="h-auto w-full"
              aria-label={`Default faculty image`}
            />
          )}

          <h2 className="text-3xl text-neutral-900 mt-4">{card.name}</h2>

          {card.occupation && (
            <div className=" text-neutral-800 font-medium tracking-[0.07rem]">
              {card.occupation}
            </div>
          )}

          {card.badge && (
            <div className="mt-4 text-primary text-lg flex items-center gap-2 font-medium tracking-[0.07rem]">
              <VerifiedCheck />
              {card.badge}
            </div>
          )}

          <div className="mt-4 flex flex-col justify-around gap-1 text-base">
            {links.twitterLink && (
              <a
                href={links.twitterLink}
                target="_blank"
                className="flex items-center gap-3 font-semibold mb-2"
              >
                <Twitter />
                Follow on X (formally Twitter)
              </a>
            )}

            {links.linkedinLink && (
              <a
                href={links.linkedinLink}
                target="_blank"
                className="flex items-center gap-3 font-semibold"
              >
                <LinkedIn /> Follow on LinkedIn
              </a>
            )}
          </div>
        </div>

        {card.bio && (
          <div className="flex h-full flex-1 flex-col pr-4 pb-12 sm:overflow-auto text-[#546277]">
            <RichText json={card.bio.json} links={card.bio.links} />
          </div>
        )}
      </div>
    </Dialog>
  )
}
